import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { FileText, Users, ImageIcon, Settings2 } from "lucide-react";

export default function AdminDashboard() {
  const { API, authHeaders } = useAuth();
  const [stats, setStats] = useState({ articles: 0, subscribers: 0, media: 0 });
  const [latest, setLatest] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/articles`),
      axios.get(`${API}/admin/subscribers`, { headers: authHeaders }),
      axios.get(`${API}/admin/media`, { headers: authHeaders }),
    ])
      .then(([arts, subs, media]) => {
        setStats({
          articles: arts.data.length,
          subscribers: subs.data.length,
          media: media.data.length,
        });
        setLatest(arts.data.slice(0, 4));
      })
      .catch(() => {});
  }, [API, authHeaders]);

  const tiles = [
    { label: "Articles", value: stats.articles, to: "/admin/articles", icon: FileText },
    { label: "Subscribers", value: stats.subscribers, to: "/admin/subscribers", icon: Users },
    { label: "Media", value: stats.media, to: "/admin/media", icon: ImageIcon },
    { label: "Site content", value: "Edit", to: "/admin/content", icon: Settings2 },
  ];

  return (
    <div data-testid="admin-dashboard">
      <p className="text-xs uppercase tracking-[0.3em] text-primary">Studio</p>
      <h1 className="mt-3 font-serif text-4xl sm:text-5xl font-light">
        Welcome back.
      </h1>
      <p className="mt-4 text-base text-muted-foreground max-w-xl">
        A quiet desk to tend the journal, the letters, and the small rituals
        that hold GentleGiTo together.
      </p>

      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {tiles.map(({ label, value, to, icon: Icon }) => (
          <Link
            key={label}
            to={to}
            data-testid={`tile-${label.toLowerCase().replace(" ", "-")}`}
            className="group bg-white/70 border border-border/60 rounded-3xl p-7 hover:border-primary/50 transition-colors"
          >
            <Icon size={18} strokeWidth={1.5} className="text-primary" />
            <p className="mt-6 font-serif text-4xl font-light">{value}</p>
            <p className="mt-2 text-xs uppercase tracking-[0.25em] text-muted-foreground">
              {label}
            </p>
          </Link>
        ))}
      </div>

      <div className="mt-16">
        <h2 className="font-serif text-2xl font-light mb-6">Latest essays</h2>
        <div className="space-y-2">
          {latest.length === 0 && (
            <p className="text-sm italic font-serif text-muted-foreground">
              No essays yet. Quiet desks are also lovely.
            </p>
          )}
          {latest.map((a) => (
            <Link
              key={a.slug}
              to={`/admin/articles/${a.slug}/edit`}
              className="flex items-center justify-between gap-4 p-4 rounded-2xl hover:bg-secondary/50 transition-colors"
            >
              <div className="min-w-0">
                <p className="font-serif text-lg truncate">{a.title}</p>
                <p className="text-xs text-muted-foreground tracking-wide uppercase mt-1">
                  {a.category} · {a.read_time}
                </p>
              </div>
              <span className="text-xs text-primary tracking-wide">Edit →</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
